import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Download,
  Star,
  User,
  Calendar,
  HardDrive,
  Copy,
  Users,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { portStyle, formatDownloads } from "@/lib/api";
import { TitleAnimation } from "@/components/TitleAnimation";

const TABS = ["Overview", "Screenshots", "Requirements"];

export const ModDetailModal = ({ mod, onClose, onDownload }) => {
  const [tab, setTab] = useState("Overview");
  const [activeShot, setActiveShot] = useState(0);

  useEffect(() => {
    setTab("Overview");
    setActiveShot(0);
  }, [mod]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = mod ? "hidden" : "";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [mod, onClose]);

  const copyCmd = () => {
    navigator.clipboard.writeText(mod.launch_command);
    toast.success("Launch command copied", { description: mod.launch_command });
  };

  return (
    <AnimatePresence>
      {mod && (
        <motion.div
          data-testid="mod-detail-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-0 sm:p-6 bg-black/80 backdrop-blur-sm overflow-y-auto"
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            transition={{ type: "spring", damping: 26, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-4xl bg-[#121115] border border-[#2e2a36] rounded-none sm:rounded-xl overflow-hidden my-0 sm:my-6"
          >
            {/* Banner */}
            <div className="relative h-48 sm:h-64 grain scanlines">
              {mod.screenshots?.[activeShot] || mod.cover_image ? (
                <img
                  src={mod.screenshots?.[activeShot] || mod.cover_image}
                  alt={mod.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-[linear-gradient(135deg,#1a0e0e_0%,#0a0a0c_55%,#0e1a12_100%)]" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#121115] via-[#121115]/40 to-transparent" />
              <button
                data-testid="modal-close-btn"
                onClick={onClose}
                className="absolute top-4 right-4 z-30 w-9 h-9 flex items-center justify-center rounded-md bg-[#0a0a0c]/80 border border-[#2e2a36] text-gray-300 hover:text-white hover:border-[#ef4444] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="absolute bottom-4 left-5 right-5 z-30">
                <span className="inline-block mb-2 px-2 py-1 rounded bg-[#0a0a0c]/80 border border-[#2e2a36] font-mono text-[9px] tracking-widest uppercase text-[#84cc16]">
                  {mod.category}
                </span>
                <h2 className="font-pixel text-2xl sm:text-3xl text-white uppercase drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)] flex items-center gap-3">
                  <TitleAnimation mod={mod} size={40} />
                  <span>{mod.title}</span>
                </h2>
              </div>
            </div>

            <div className="p-5 sm:p-7">
              {/* Meta row */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-5 text-xs font-mono text-gray-400">
                <span className="flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5 text-[#84cc16]" /> {mod.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-[#84cc16]" /> {mod.year}
                </span>
                {mod.size && (
                  <span className="flex items-center gap-1.5">
                    <HardDrive className="w-3.5 h-3.5 text-[#84cc16]" /> {mod.size}
                  </span>
                )}
                {mod.rating > 0 && (
                  <span className="flex items-center gap-1.5 text-[#f59e0b]">
                    <Star className="w-3.5 h-3.5 fill-[#f59e0b]" />
                    {mod.rating.toFixed(1)}
                  </span>
                )}
                <span className="flex items-center gap-1.5 text-[#f59e0b]">
                  <Download className="w-3.5 h-3.5" />
                  {formatDownloads(mod.downloads)}
                </span>
                {mod.multiplayer && (
                  <span className="flex items-center gap-1.5 text-[#a3e635]">
                    <Users className="w-3.5 h-3.5" /> Multiplayer
                  </span>
                )}
              </div>

              {/* Tabs */}
              <div className="flex gap-1 border-b border-[#2e2a36] mb-5">
                {TABS.map((t) => (
                  <button
                    key={t}
                    data-testid={`modal-tab-${t.toLowerCase()}`}
                    onClick={() => setTab(t)}
                    className={`px-4 py-2.5 font-mono text-[11px] uppercase tracking-wider transition-colors border-b-2 -mb-px ${
                      tab === t
                        ? "border-[#ef4444] text-white"
                        : "border-transparent text-gray-500 hover:text-gray-300"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>

              <div className="min-h-[160px]">
                {tab === "Overview" && (
                  <div>
                    <p className="text-sm text-gray-300 font-body leading-relaxed mb-4">
                      {mod.description}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {mod.tags.map((tg) => (
                        <span
                          key={tg}
                          className="px-2 py-1 rounded bg-[#18161d] border border-[#2e2a36] font-mono text-[10px] text-gray-400"
                        >
                          #{tg}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {tab === "Screenshots" && (
                  <div>
                    {mod.screenshots?.length ? (
                      <>
                        <div className="aspect-video rounded-lg overflow-hidden border border-[#2e2a36] mb-3">
                          <img
                            src={mod.screenshots[activeShot]}
                            alt="screenshot"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex gap-2">
                          {mod.screenshots.map((s, i) => (
                            <button
                              key={s}
                              data-testid={`screenshot-thumb-${i}`}
                              onClick={() => setActiveShot(i)}
                              className={`w-20 h-14 rounded overflow-hidden border-2 transition-all ${
                                activeShot === i
                                  ? "border-[#ef4444]"
                                  : "border-[#2e2a36] opacity-60 hover:opacity-100"
                              }`}
                            >
                              <img src={s} alt="" className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      </>
                    ) : (
                      <p className="text-xs font-mono text-gray-500 py-8 text-center">
                        No screenshots available — visit the host to preview this WAD.
                      </p>
                    )}
                  </div>
                )}

                {tab === "Requirements" && (
                  <div className="space-y-4">
                    <div>
                      <div className="text-[10px] font-mono tracking-widest uppercase text-gray-500 mb-2">
                        Compatible Source Ports
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {mod.source_ports.length ? (
                          mod.source_ports.map((p) => {
                            const s = portStyle(p);
                            return (
                              <span
                                key={p}
                                className="px-2.5 py-1 rounded font-mono text-[10px] tracking-wider uppercase border"
                                style={{ background: s.bg, borderColor: s.border, color: s.text }}
                              >
                                {p}
                              </span>
                            );
                          })
                        ) : (
                          <span className="text-xs font-mono text-gray-500">
                            Check the host listing for compatibility.
                          </span>
                        )}
                      </div>
                    </div>
                    {mod.launch_command && (
                      <div>
                        <div className="text-[10px] font-mono tracking-widest uppercase text-gray-500 mb-2">
                          Launch Command
                        </div>
                        <div className="flex items-center gap-2 bg-[#0a0a0c] border border-[#2e2a36] rounded px-3 py-2.5">
                          <code className="flex-1 text-xs font-mono text-[#84cc16] overflow-x-auto whitespace-nowrap">
                            $ {mod.launch_command}
                          </code>
                          <button
                            data-testid="copy-launch-cmd-btn"
                            onClick={copyCmd}
                            className="shrink-0 text-gray-500 hover:text-[#84cc16] transition-colors"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3 mt-6 pt-5 border-t border-[#2e2a36]">
                <button
                  data-testid="modal-download-btn"
                  onClick={() => onDownload(mod)}
                  className="group flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-[#ef4444] hover:bg-[#dc2626] text-white font-pixel text-xs uppercase tracking-wider px-6 py-3.5 rounded-md transition-all hover:shadow-[0_0_24px_rgba(239,68,68,0.5)]"
                >
                  <Download className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
                  Download WAD
                </button>
                <a
                  data-testid="modal-visit-host-link"
                  href={mod.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 border border-[#2e2a36] hover:border-[#06b6d4] text-gray-300 hover:text-[#22d3ee] font-mono text-xs uppercase tracking-wider px-5 py-3.5 rounded-md transition-colors"
                >
                  Visit Host
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
